import {
  AttachMoney,
  Description,
  History as HistoryIcon,
  Info as InfoIcon,
  Percent,
} from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { getDocumentReport } from 'lib/api/document';
import React from 'react';
import { useDispatch } from 'react-redux';

import {
  FactoringExtension,
  FactoringResellWithFund,
} from '../../../../../services/redux/documentProfileSlice.types';
import { formatDate, formatMoney } from '../../../../functions/utils';
import InfoRow from './InfoRow';
import SectionSkeleton from './SectionSkeleton';
import ExtensionsTooltip from './Tooltips/ExtensionsTooltip';
import FactoringResellTooltip from './Tooltips/FactoringResellTooltip';
import { DocumentConciliationRateHistory } from './types';

const formatRate = (rate: string | null): string | null => {
  if (!rate) return null;
  return `${rate}%`;
};

interface SummarySectionProps {
  documentId: string;
  rateHistory?: DocumentConciliationRateHistory | null;
  onHistoryLoaded?: (history: DocumentConciliationRateHistory) => void;
  extensions?: FactoringExtension[];
}

const SummarySection: React.FC<SummarySectionProps> = ({
  documentId,
  rateHistory,
  onHistoryLoaded,
}) => {
  const dispatch = useDispatch();
  const { data: document, isPending } = getDocumentReport(dispatch, documentId);

  if (!document || isPending) return <SectionSkeleton rowsPerColumn={6} />;

  const {
    operationDate,
    rate,
    arrearRate,
    invoiceAmount,
    advance,
    amount,
    amountNet,
    paymentDate,
    daysToCount,
    fundName,
    costRate,
    extensions,
  } = document;

  const factoringResells = (
    document as { factoringResells?: FactoringResellWithFund[] }
  ).factoringResells;

  function getRetainedAmount() {
    if (!amount || !amountNet) return '';
    return formatMoney(amount - amountNet);
  }

  const hasExtensions = extensions && extensions.length > 0;
  const firstResell = factoringResells?.[0];
  const hasResells = !!firstResell;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(156, 39, 176, 0.08)',
          borderRadius: '8px',
          px: 1.5,
          py: 0.8,
          border: '1px solid rgba(156, 39, 176, 0.15)',
          mt: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'purple',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          Resumen Operación
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoRow
            label="Fecha Operación"
            value={formatDate(new Date(operationDate ?? 0), 'dd-mm-yyyy')}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Tasa negocio"
            value={formatRate(rate.toString())}
            maxLength={40}
            icon={<Percent sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Tasa morosidad"
            value={formatRate(arrearRate?.toString() || null)}
            maxLength={40}
            icon={<Percent sx={{ fontSize: '0.8rem', color: 'purple' }} />}
            actionIcon={<HistoryIcon sx={{ fontSize: '1rem' }} />}
            actionTooltipText="Historial de tasas aplicadas"
            documentId={documentId}
            rateHistory={rateHistory}
            onHistoryLoaded={onHistoryLoaded}
          />
          <InfoRow
            label="Monto Original"
            value={invoiceAmount ? formatMoney(invoiceAmount) : null}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Anticipo"
            value={formatRate(advance.toString())}
            maxLength={40}
            icon={<Percent sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Monto Retenido"
            value={getRetainedAmount()}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoRow
            label="Fecha de vencimiento"
            value={formatDate(new Date(paymentDate), 'dd-mm-yyyy')}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
            actionIcon={
              hasExtensions ? (
                <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              ) : undefined
            }
            customTooltip={
              hasExtensions ? (
                <ExtensionsTooltip extensions={extensions} />
              ) : undefined
            }
          />
          <InfoRow
            label="Días"
            value={daysToCount?.toString() || null}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Fondo"
            value={fundName || null}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
            actionIcon={
              hasResells ? (
                <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              ) : undefined
            }
            customTooltip={
              firstResell ? (
                <FactoringResellTooltip resell={firstResell} />
              ) : undefined
            }
          />
          <InfoRow
            label="Monto Documento"
            value={amount ? formatMoney(amount) : null}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Monto Anticipado"
            value={formatMoney(amountNet)}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Costo de Interés"
            value={costRate ? formatMoney(costRate) : null}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SummarySection;
